import React from 'react';
import { Row, Col, Form, Button, Table, Card, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faFileInvoice,
  faSave,
  faTimes,
  faPlus,
  faCalculator,
  faPrint
} from '@fortawesome/free-solid-svg-icons';
import { SortableHeader } from '../../../ui/SortableHeader/SortableHeader';
import { Helpers } from '../../../../utils/helpers';
import { IClubMemberDto } from '../../../../utils/constants/interfaces';
import { useInvoices } from './useInvoices';
import styles from './InvoiceSection.module.css';

interface InvoiceSectionProps {
  clubMemberId: string;
  fieldErrors: string[];
  setFieldErrors: React.Dispatch<React.SetStateAction<string[]>>;
  memberFirstName?: string;
  memberLastName?: string;
  showHeader?: boolean;
}

interface InvoiceSectionProps {
  clubMemberId: string;
  fieldErrors: string[];
  setFieldErrors: React.Dispatch<React.SetStateAction<string[]>>;
  memberFirstName?: string;
  memberLastName?: string;
  clubMember?: IClubMemberDto; // Add club member data for printing
  showHeader?: boolean;
}

export const InvoiceSection: React.FC<InvoiceSectionProps> = ({
  clubMemberId,
  fieldErrors,
  setFieldErrors,
  memberFirstName,
  memberLastName,
  clubMember,
  showHeader = false
}) => {
  const invoicesHook = useInvoices({
    clubMemberId,
    fieldErrors,
    setFieldErrors,
    clubMember // Pass club member data to hook
  });

  const {
    loadingInvoices,
    savingInvoice,
    generatingInvoice,
    invoiceFormData,
    invoiceFormMode,
    selectedInvoiceRow,
    invoiceSortField,
    invoiceSortDirection,
    updateInvoiceFormData,
    clearInvoiceForm,
    selectInvoiceForEdit,
    saveInvoice,
    generateInvoice,
    recalculateInvoice,
    printInvoice,
    handleInvoiceSort,
    getSortedInvoices,
    canGenerateInvoice
  } = invoicesHook;

  const isNew = Helpers.isNullOrWhiteSpace(clubMemberId) || clubMemberId === 'new';

  if (isNew) {
    return (
      <div className={styles.placeholderContent}>
        <FontAwesomeIcon icon={faFileInvoice} className={styles.placeholderIcon} />
        <div className={styles.placeholderText}>Invoice management available after save</div>
        <div className={styles.placeholderSubtext}>
          Save the member information first to start managing invoices
        </div>
      </div>
    );
  }

  const sortedInvoices = getSortedInvoices();

  return (
    <>
      {/* Header with member name (only show when requested) */}
      {showHeader && memberFirstName && memberLastName && (
        <div className="d-flex align-items-center mb-4 mx-3">
          <h3 className="mb-0">
            Invoices for: {memberFirstName} {memberLastName}
          </h3>
        </div>
      )}

      {/* Invoice Form */}
      <Card className={`mb-4 ${styles.invoiceFormCard}`}>
        <Card.Header>
          <h5 className="mb-0">
            <FontAwesomeIcon icon={faFileInvoice} className="me-2" />
            {invoiceFormMode === 'edit' ? 'Edit Invoice' : 'Invoice Details'}
          </h5>
        </Card.Header>
        <Card.Body>
          <Form>
            {/* First row - Invoice ID, Billing Year, Total Amount */}
            <Row>
              <Col md={2}>
                <Form.Group className="mb-3">
                  <Form.Label>Invoice ID</Form.Label>
                  <div className={styles.fieldLabel}>
                    {invoiceFormData.id || (invoiceFormMode === 'disabled' ? 'None selected' : '')}
                  </div>
                </Form.Group>
              </Col>
              <Col md={2}>
                <Form.Group className="mb-3">
                  <Form.Label>Billing Year</Form.Label>
                  <div className={styles.fieldLabel}>{invoiceFormData.billingYear}</div>
                </Form.Group>
              </Col>
              <Col md={8}>
                <Form.Group className="mb-3">
                  <Form.Label>Total Amount</Form.Label>
                  <div className={styles.totalAmountLabel}>${invoiceFormData.totalAmount}</div>
                </Form.Group>
              </Col>
            </Row>

            {/* Second row - Fee fields */}
            <Row>
              <Col md={2}>
                <Form.Group className="mb-3">
                  <Form.Label>Dues</Form.Label>
                  <Form.Control
                    type="number"
                    step="0.01"
                    min="0"
                    value={invoiceFormData.dues}
                    onChange={(e) => updateInvoiceFormData('dues', e.target.value)}
                    isInvalid={fieldErrors.includes('invoices:dues')}
                    disabled={invoiceFormMode === 'disabled'}
                  />
                  <Form.Control.Feedback type="invalid">
                    Dues must be a valid positive number (blank = 0).
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col md={2}>
                <Form.Group className="mb-3">
                  <Form.Label>Late Fee</Form.Label>
                  <Form.Control
                    type="number"
                    step="0.01"
                    min="0"
                    value={invoiceFormData.lateFee}
                    onChange={(e) => updateInvoiceFormData('lateFee', e.target.value)}
                    isInvalid={fieldErrors.includes('invoices:lateFee')}
                    disabled={invoiceFormMode === 'disabled'}
                  />
                  <Form.Control.Feedback type="invalid">
                    Late fee must be a valid positive number.
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col md={2}>
                <Form.Group className="mb-3">
                  <Form.Label>Initiation Fee</Form.Label>
                  <Form.Control
                    type="number"
                    step="0.01"
                    min="0"
                    value={invoiceFormData.initiationFee}
                    onChange={(e) => updateInvoiceFormData('initiationFee', e.target.value)}
                    isInvalid={fieldErrors.includes('invoices:initiationFee')}
                    disabled={invoiceFormMode === 'disabled'}
                  />
                  <Form.Control.Feedback type="invalid">
                    Initiation fee must be a valid positive number.
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col md={2}>
                <Form.Group className="mb-3">
                  <Form.Label>Other Fee</Form.Label>
                  <Form.Control
                    type="number"
                    step="0.01"
                    min="0"
                    value={invoiceFormData.otherFee}
                    onChange={(e) => updateInvoiceFormData('otherFee', e.target.value)}
                    isInvalid={fieldErrors.includes('invoices:otherFee')}
                    disabled={invoiceFormMode === 'disabled'}
                  />
                  <Form.Control.Feedback type="invalid">
                    Other fee must be a valid positive number.
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col md={2}>
                <Form.Group className="mb-3">
                  <Form.Label>Work Hours</Form.Label>
                  <Form.Control
                    type="number"
                    min="0"
                    value={invoiceFormData.workHours}
                    onChange={(e) => updateInvoiceFormData('workHours', e.target.value)}
                    isInvalid={fieldErrors.includes('invoices:workHours')}
                    disabled={invoiceFormMode === 'disabled'}
                  />
                  <Form.Control.Feedback type="invalid">
                    Work hours must be a valid positive number.
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
            </Row>

            {/* Third row - Member type and discount fields */}
            <Row>
              <Col md={3}>
                <Form.Group className="mb-3">
                  <Form.Label>Member Type</Form.Label>
                  <Form.Control
                    type="text"
                    value={invoiceFormData.memberTypeName}
                    onChange={(e) => updateInvoiceFormData('memberTypeName', e.target.value)}
                    disabled={invoiceFormMode === 'disabled'}
                    placeholder="Member type..."
                    maxLength={100}
                  />
                </Form.Group>
              </Col>
              <Col md={2}>
                <Form.Group className="mb-3">
                  <Form.Label>Base Dues for Type</Form.Label>
                  <Form.Control
                    type="number"
                    step="0.01"
                    min="0"
                    value={invoiceFormData.memberTypeAmount}
                    onChange={(e) => updateInvoiceFormData('memberTypeAmount', e.target.value)}
                    isInvalid={fieldErrors.includes('invoices:memberTypeAmount')}
                    disabled={invoiceFormMode === 'disabled'}
                  />
                  <Form.Control.Feedback type="invalid">
                    Base dues must be a valid positive number.
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col md={2}>
                <Form.Group className="mb-3">
                  <Form.Label>Discount Applied</Form.Label>
                  <div className="input-group">
                    <Form.Control
                      type="number"
                      step="0.01"
                      min="0"
                      value={invoiceFormData.discountApplied}
                      onChange={(e) => updateInvoiceFormData('discountApplied', e.target.value)}
                      isInvalid={fieldErrors.includes('invoices:discountApplied')}
                      disabled={invoiceFormMode === 'disabled'}
                    />
                    <span className="input-group-text">%</span>
                  </div>
                  <Form.Control.Feedback type="invalid">
                    Discount must be a valid positive percentage.
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col md={5}>
                <Form.Group className="mb-3">
                  <Form.Label>Discount Reason</Form.Label>
                  <Form.Control
                    type="text"
                    value={invoiceFormData.discountReason}
                    onChange={(e) => updateInvoiceFormData('discountReason', e.target.value)}
                    disabled={invoiceFormMode === 'disabled'}
                    placeholder="Reason for discount..."
                    maxLength={250}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={12}>
                <Form.Group className="mb-3">
                  <Form.Label>Notes</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    value={invoiceFormData.notes}
                    onChange={(e) => updateInvoiceFormData('notes', e.target.value)}
                    placeholder="Invoice notes..."
                    maxLength={500}
                    disabled={invoiceFormMode === 'disabled'}
                  />
                </Form.Group>
              </Col>
            </Row>

            {/* Action Buttons */}
            <div className={styles.buttonGroup}>
              <Button
                variant="success"
                onClick={saveInvoice}
                disabled={invoiceFormMode === 'disabled' || savingInvoice || generatingInvoice}
                className="me-2">
                {savingInvoice && <Spinner size="sm" className="me-2" />}
                <FontAwesomeIcon icon={faSave} className="me-2" />
                Save
              </Button>

              <Button
                variant="outline-secondary"
                onClick={clearInvoiceForm}
                disabled={savingInvoice || generatingInvoice}
                className="me-2">
                <FontAwesomeIcon icon={faTimes} className="me-2" />
                Cancel
              </Button>

              <Button
                variant="primary"
                onClick={generateInvoice}
                disabled={!canGenerateInvoice() || savingInvoice || generatingInvoice}
                className={`me-2 ${styles.generateButton}`}>
                {generatingInvoice && <Spinner size="sm" className="me-2" />}
                <FontAwesomeIcon icon={faPlus} className="me-2" />
                Generate Invoice
              </Button>

              <Button
                variant="warning"
                onClick={recalculateInvoice}
                disabled={invoiceFormMode === 'disabled' || savingInvoice || generatingInvoice}
                className={`me-2 ${styles.recalculateButton}`}>
                {generatingInvoice && <Spinner size="sm" className="me-2" />}
                <FontAwesomeIcon icon={faCalculator} className="me-2" />
                Recalculate
              </Button>

              <Button
                variant="info"
                onClick={printInvoice}
                disabled={invoiceFormMode === 'disabled'}
                className={styles.printButton}>
                <FontAwesomeIcon icon={faPrint} className="me-2" />
                Print
              </Button>
            </div>
          </Form>
        </Card.Body>
      </Card>

      {/* Invoice History Table */}
      <Card className={styles.tableContainer}>
        <Card.Header>
          <h5 className="mb-0">
            <FontAwesomeIcon icon={faFileInvoice} className="me-2" />
            Invoice History
          </h5>
        </Card.Header>
        <Card.Body className="p-0">
          {loadingInvoices ? (
            <div className="text-center p-4">
              <Spinner animation="border" />
              <div className="mt-2">Loading invoices...</div>
            </div>
          ) : (
            <Table striped hover responsive className="mb-0">
              <thead>
                <tr>
                  <SortableHeader
                    text="Year"
                    sortField="billingYear"
                    sortDirection={invoiceSortDirection}
                    active={invoiceSortField === 'billingYear'}
                    onClick={handleInvoiceSort}
                  />
                  <SortableHeader
                    text="Total Amount"
                    sortField="totalAmount"
                    sortDirection={invoiceSortDirection}
                    active={invoiceSortField === 'totalAmount'}
                    onClick={handleInvoiceSort}
                  />
                  <SortableHeader
                    text="Dues"
                    sortField="dues"
                    sortDirection={invoiceSortDirection}
                    active={invoiceSortField === 'dues'}
                    onClick={handleInvoiceSort}
                  />
                  <SortableHeader
                    text="Late Fee"
                    sortField="lateFee"
                    sortDirection={invoiceSortDirection}
                    active={invoiceSortField === 'lateFee'}
                    onClick={handleInvoiceSort}
                  />
                  <SortableHeader
                    text="Initiation Fee"
                    sortField="initiationFee"
                    sortDirection={invoiceSortDirection}
                    active={invoiceSortField === 'initiationFee'}
                    onClick={handleInvoiceSort}
                  />
                  <SortableHeader
                    text="Other Fee"
                    sortField="otherFee"
                    sortDirection={invoiceSortDirection}
                    active={invoiceSortField === 'otherFee'}
                    onClick={handleInvoiceSort}
                  />
                  <SortableHeader
                    text="Work Hours"
                    sortField="workHours"
                    sortDirection={invoiceSortDirection}
                    active={invoiceSortField === 'workHours'}
                    onClick={handleInvoiceSort}
                  />
                  <th>Notes</th>
                  <SortableHeader
                    text="Member Type"
                    sortField="memberTypeName"
                    sortDirection={invoiceSortDirection}
                    active={invoiceSortField === 'memberTypeName'}
                    onClick={handleInvoiceSort}
                  />
                  <SortableHeader
                    text="Type Amount"
                    sortField="memberTypeAmount"
                    sortDirection={invoiceSortDirection}
                    active={invoiceSortField === 'memberTypeAmount'}
                    onClick={handleInvoiceSort}
                  />
                  <SortableHeader
                    text="Discount"
                    sortField="discountApplied"
                    sortDirection={invoiceSortDirection}
                    active={invoiceSortField === 'discountApplied'}
                    onClick={handleInvoiceSort}
                  />
                  <th>Discount Reason</th>
                  <SortableHeader
                    text="Entered Date"
                    sortField="dateCreatedUtc"
                    sortDirection={invoiceSortDirection}
                    active={invoiceSortField === 'dateCreatedUtc'}
                    onClick={handleInvoiceSort}
                  />
                  <SortableHeader
                    text="Entered By"
                    sortField="createdBy"
                    sortDirection={invoiceSortDirection}
                    active={invoiceSortField === 'createdBy'}
                    onClick={handleInvoiceSort}
                  />
                  <SortableHeader
                    text="Updated Date"
                    sortField="lastModifiedDateUtc"
                    sortDirection={invoiceSortDirection}
                    active={invoiceSortField === 'lastModifiedDateUtc'}
                    onClick={handleInvoiceSort}
                  />
                  <SortableHeader
                    text="Updated By"
                    sortField="lastModifiedBy"
                    sortDirection={invoiceSortDirection}
                    active={invoiceSortField === 'lastModifiedBy'}
                    onClick={handleInvoiceSort}
                  />
                </tr>
              </thead>
              <tbody>
                {sortedInvoices.length === 0 ? (
                  <tr>
                    <td colSpan={16} className="text-center text-muted py-4">
                      No invoices found. Click Generate Invoice to create the first invoice.
                    </td>
                  </tr>
                ) : (
                  sortedInvoices.map((invoice, index) => {
                    const totalAmount =
                      invoice.dues + invoice.lateFee + invoice.initiationFee + invoice.otherFee;
                    const isSelected = selectedInvoiceRow === index;
                    return (
                      <tr
                        key={invoice.id}
                        className={`${styles.clickableRow} ${isSelected ? styles.selectedRow : ''}`}
                        onClick={() => selectInvoiceForEdit(invoice, index)}>
                        <td>{invoice.billingYear}</td>
                        <td className={styles.amountCell}>${totalAmount.toFixed(2)}</td>
                        <td className={styles.amountCell}>${invoice.dues.toFixed(2)}</td>
                        <td className={styles.amountCell}>${invoice.lateFee.toFixed(2)}</td>
                        <td className={styles.amountCell}>${invoice.initiationFee.toFixed(2)}</td>
                        <td className={styles.amountCell}>${invoice.otherFee.toFixed(2)}</td>
                        <td>{invoice.workHours}</td>
                        <td>{invoice.notes || '-'}</td>
                        <td>{invoice.memberTypeName}</td>
                        <td className={styles.amountCell}>
                          ${invoice.memberTypeAmount.toFixed(2)}
                        </td>
                        <td className={styles.amountCell}>
                          {invoice.discountApplied > 0
                            ? `${invoice.discountApplied.toFixed(2)}%`
                            : '0%'}
                        </td>
                        <td>{invoice.discountReason || '-'}</td>
                        <td>{Helpers.toShortDate(invoice.dateCreatedUtc)}</td>
                        <td>{invoice.createdBy}</td>
                        <td>
                          {invoice.lastModifiedDateUtc
                            ? Helpers.toShortDate(invoice.lastModifiedDateUtc)
                            : '-'}
                        </td>
                        <td>{invoice.lastModifiedBy || '-'}</td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>
    </>
  );
};
